import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Download, FileText, PackageCheck, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";

function B2BOrders() {
  const navigate = useNavigate();
  const brand = useMemo(
    () => ({
      bg: "#F7FAFC",
      text: "#0F172A",
      subtext: "rgba(15, 23, 42, 0.72)",
      stroke: "rgba(2, 6, 23, 0.10)",
      accent: "#0EA5E9",
      accent2: "#22C55E",
    }),
    []
  );

  const rows = useMemo(
    () => [
      {
        id: "ORD-1182",
        status: "Delivered",
        total: "₹18,400",
        items: "Safety Helmet (Bulk Pack) x 12",
        invoice: "INV-2026-0412",
      },
      {
        id: "ORD-1190",
        status: "In transit",
        total: "₹9,200",
        items: "Packaging Tape (Case) x 80",
        invoice: "INV-2026-0419",
      },
      {
        id: "ORD-1201",
        status: "Processing",
        total: "₹6,150",
        items: "Wi‑Fi Router (Business) x 6",
        invoice: "INV-2026-0421",
      },
    ],
    []
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.bg, py: 6 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${brand.stroke}`,
            backgroundImage:
              'linear-gradient(90deg, rgba(14,165,233,0.12), rgba(34,197,94,0.10)), url("https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1400&h=360&fit=crop")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 950, color: brand.text }}>
                  Orders & Invoices
                </Typography>
                <Typography sx={{ color: brand.subtext, mt: 0.5 }}>
                  Track deliveries, download invoices, and view order history.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/B2BShop")}
                  sx={{
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 950,
                    borderColor: brand.stroke,
                    color: brand.text,
                  }}
                >
                  Browse catalog
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 950,
                    color: "white",
                    background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                  }}
                >
                  Download summary
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(14,165,233,0.10)" }}>
                    <Truck size={18} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: brand.text }}>In transit</Typography>
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      1 shipment moving
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(34,197,94,0.10)" }}>
                    <PackageCheck size={18} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: brand.text }}>Delivered</Typography>
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      1 completed this week
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(2,6,23,0.06)" }}>
                    <FileText size={18} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: brand.text }}>Invoices</Typography>
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      3 available downloads
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ borderRadius: 4, border: `1px solid ${brand.stroke}` }}>
              <CardContent>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" sx={{ mb: 1 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: brand.text }}>Recent orders</Typography>
                    <Typography variant="body2" sx={{ color: brand.subtext }}>
                      Click an order to open a product (demo).
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 950, borderColor: brand.stroke, color: brand.text }}
                    startIcon={<Download size={16} />}
                  >
                    Export CSV
                  </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1.25}>
                  {rows.map((r) => (
                    <Box
                      key={r.id}
                      sx={{
                        p: 1.75,
                        borderRadius: 3,
                        bgcolor: "white",
                        border: `1px solid ${brand.stroke}`,
                      }}
                    >
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <Typography sx={{ fontWeight: 950, color: brand.text }}>
                            {r.id}
                          </Typography>
                          <Typography variant="caption" sx={{ color: brand.subtext }}>
                            {r.invoice}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <Typography sx={{ fontWeight: 800, color: brand.text }} noWrap>
                            {r.items}
                          </Typography>
                          <Typography variant="body2" sx={{ color: brand.subtext }}>
                            Total: {r.total}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Chip
                            size="small"
                            label={r.status}
                            color={
                              r.status === "Delivered"
                                ? "success"
                                : r.status === "In transit"
                                  ? "info"
                                  : "warning"
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: "none", fontWeight: 900, borderColor: brand.stroke, color: brand.text }}
                            >
                              Invoice
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => navigate("/B2BShop")}
                              sx={{
                                textTransform: "none",
                                fontWeight: 900,
                                color: "white",
                                background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                              }}
                            >
                              Reorder
                            </Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default B2BOrders;

